import { RECORDABLE_COMMAND_IDS, type MacroBundleV1, type MacroStepV1, type StoredMacroV1 } from "../types";

const ownKeys = (value: object, allowed: readonly string[]) => Object.keys(value).every((key) => allowed.includes(key));
const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
const isDate = (value: unknown): value is string => typeof value === "string" && !Number.isNaN(Date.parse(value));

export function isMacroStep(value: unknown): value is MacroStepV1 {
  if (!isObject(value) || typeof value.kind !== "string") return false;
  if (value.kind === "insert") return ownKeys(value, ["kind", "text"]) && typeof value.text === "string";
  if (value.kind === "command") return ownKeys(value, ["kind", "command"]) && RECORDABLE_COMMAND_IDS.includes(value.command as never);
  if (value.kind !== "search" || !ownKeys(value, ["kind", "action", "query"])) return false;
  if (!["find-next", "find-previous", "replace-next", "replace-all"].includes(String(value.action)) || !isObject(value.query)) return false;
  const query = value.query;
  return ownKeys(query, ["query", "replacement", "caseSensitive", "wholeWord", "regexp"])
    && typeof query.query === "string" && typeof query.replacement === "string"
    && typeof query.caseSensitive === "boolean" && typeof query.wholeWord === "boolean" && typeof query.regexp === "boolean";
}

export function isStoredMacro(value: unknown): value is StoredMacroV1 {
  if (!isObject(value) || !ownKeys(value, ["schemaVersion", "id", "name", "createdAt", "updatedAt", "steps"])) return false;
  return value.schemaVersion === 1 && typeof value.id === "string" && value.id.length > 0
    && typeof value.name === "string" && value.name.trim().length > 0
    && isDate(value.createdAt) && isDate(value.updatedAt)
    && Array.isArray(value.steps) && value.steps.every(isMacroStep);
}

export function parseMacroBundle(json: string): MacroBundleV1 {
  let value: unknown;
  try { value = JSON.parse(json); } catch { throw new Error("The selected file is not valid JSON."); }
  if (!isObject(value) || !ownKeys(value, ["schemaVersion", "exportedAt", "macros"]) || value.schemaVersion !== 1) {
    throw new Error("Unsupported or malformed macro bundle.");
  }
  if (!isDate(value.exportedAt) || !Array.isArray(value.macros) || !value.macros.every(isStoredMacro)) {
    throw new Error("The macro bundle failed validation.");
  }
  return value as unknown as MacroBundleV1;
}

export function mergeImportedMacros(existing: StoredMacroV1[], imported: StoredMacroV1[]): StoredMacroV1[] {
  const result = [...existing];
  const names = new Set(result.map((macro) => macro.name.toLocaleLowerCase()));
  const ids = new Set(result.map((macro) => macro.id));
  for (const macro of imported) {
    let name = macro.name;
    let suffix = 2;
    while (names.has(name.toLocaleLowerCase())) name = `${macro.name} (${suffix++})`;
    const id = ids.has(macro.id) ? crypto.randomUUID() : macro.id;
    const copy = { ...macro, id, name, steps: macro.steps.map((step) => structuredClone(step)) };
    result.push(copy); names.add(name.toLocaleLowerCase()); ids.add(id);
  }
  return result;
}
